import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ChevronDown } from "lucide-react";
import EmailViewSelector from "./EmailViewSelector";
import EmailFiltersControl from "./EmailFiltersControl";

// DASH-EMAIL-P1-IMPL-006: canonical 50-state + DC vocabulary, matching
// backend/app/services/email_delivery_geography.py's exact scope (no
// territories). Moved here from EmailOverviewView.jsx now that the
// governed State filter renders inside this shared bar's Advanced row
// rather than a separate card.
const US_STATE_OPTIONS = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"],
  ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
  ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"],
  ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

// Local focus-ring override (Checkpoint 3.4C/3.4D pattern) — the shared
// ui/select.jsx primitive's `focus:ring-1 focus:ring-ring` resolves to a
// near-white ring in this app's dark theme and stays visually "stuck"
// after Radix returns focus to a trigger post-selection (including a
// plain mouse click). Every trigger in this bar carries this override
// rather than patching the shared primitive — see EmailViewSelector.jsx
// for the full root-cause writeup.
const SELECT_FOCUS_RING_FIX = "focus:ring-0 focus-visible:ring-2 focus-visible:ring-indigo-400";

// Checkpoint 3.4F remediation: an adversarial review found that the prior
// design (two INDEPENDENT flex-wrap rows, each reusing an identical
// grid-template-columns class) could not actually guarantee alignment —
// row 1 also carried Apply/Advanced/conditional-Reset/EmailFiltersControl,
// so it had a smaller free-space pool than row 2, and CSS flexbox
// distributes free space per-container. Two containers with identical
// column definitions but different total sibling content generally
// compute DIFFERENT column widths/offsets, even though their class
// strings match byte-for-byte.
//
// The fix: ONE CSS Grid container is the single source of truth for every
// column boundary. Both the primary row's cells (Email/Period/
// Environment/Broadcast/Actions, auto-placed by DOM order into columns
// 1-5) and the advanced row's cells (Program/Campaign, EXPLICITLY placed
// via lg:col-start-3 / lg:col-start-4) are direct children of this same
// grid — so "column 3" and "column 4" resolve to one set of pixel
// boundaries shared by construction, not by two independently-computed
// layouts happening to agree. Program can never land under Period, and
// nothing can land under Email/Period, because those two columns are
// simply never targeted by any advanced-row cell's column-start — not
// because a spacer visually occupies the space.
//
// Below `lg` (1024px — this codebase's existing "full desktop" breakpoint;
// see the KPI grids in EmailOverviewView.jsx/EmailPerformanceView.jsx/
// AnalyticsWarRoom.jsx, which all top out their responsive scaling at
// `lg:`), the grid collapses to a single stacked column instead of
// forcing the desktop template to overflow a narrow viewport.
//
// Checkpoint 3.5A remediation: an adversarial review EMPIRICALLY proved
// (via the real Tailwind CLI, not jsdom) that a PRIOR version of this file
// built the `lg:grid-cols-[...]` arbitrary-value class at runtime via
// string interpolation (`` `lg:grid-cols-[${tracks.join("_")}]` ``).
// Tailwind's content scanner does not execute JavaScript — it does a
// static text scan for complete, literal class-name substrings. A
// runtime-interpolated string is invisible to it, so NO grid-template-
// columns CSS was emitted for EITHER configuration, silently reverting
// the desktop grid to grid-cols-1 (mobile) at every viewport width in a
// real build — a regression to the already-approved 3.4F layout, not
// merely a Deliverability-scoped gap.
//
// The fix: every possible desktop track-list is now a COMPLETE, STATIC
// source literal — Tailwind's scanner finds each one verbatim, regardless
// of which is selected at runtime. FULL_FILTER_GRID_CLASS is byte-for-byte
// the same string 3.4F shipped and already had adversarially reviewed.
const FULL_FILTER_GRID_CLASS =
  "grid grid-cols-1 items-center gap-3 lg:grid-cols-[minmax(130px,1fr)_minmax(130px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_auto]";
const ENVIRONMENT_ONLY_FILTER_GRID_CLASS =
  "grid grid-cols-1 items-center gap-3 lg:grid-cols-[minmax(130px,1fr)_minmax(100px,1fr)_auto]";
// DASH-EMAIL-004: Audience & Acquisition's config — Email/Period/
// Environment/Actions, 4 columns. A complete, independent static literal
// (same Tailwind-JIT-scanner requirement as the two constants above), not
// derived from either of them.
const PERIOD_ENVIRONMENT_FILTER_GRID_CLASS =
  "grid grid-cols-1 items-center gap-3 lg:grid-cols-[minmax(130px,1fr)_minmax(130px,1fr)_minmax(100px,1fr)_auto]";
// DASH-EMAIL-P1-IMPL-008A: Email/Actions only, 2 columns -- for a view with
// NO operative filter dimension at all (see `unavailable` below). A
// complete, independent static literal (same Tailwind-JIT-scanner
// requirement as the other three above).
const NO_OPERATIONAL_FILTERS_GRID_CLASS = "grid grid-cols-1 items-center gap-3 lg:grid-cols-[minmax(130px,1fr)_auto]";

// Checkpoint 3.5A: declarative field configuration, replacing what would
// otherwise be a pile of `view === "deliverability"` checks scattered
// through this file's JSX. EmailDashboardPanel.jsx is the ONLY place that
// inspects `view` to choose between these two objects; everything below
// this point just reads `fields.*` flags, unaware of which view it's
// rendering for. `full` (Overview/Performance) is unchanged from 3.4F —
// every field is visible, exactly as before, using the exact same grid
// class 3.4F shipped. `environmentOnly` (Deliverability) exists because
// SendGridRecipientState — the authoritative recipient-state model — has
// no Period/Broadcast/Program/Campaign concept at all (see
// emailDeliverabilityMockAdapter.js's doc comment); showing those controls
// there would imply a query semantic the backend cannot support. Each
// entry carries its own COMPLETE static `gridClass` literal (see above) —
// adding a third configuration in the future means adding a third
// complete literal, not parameterizing this one.
export const EMAIL_FILTER_BAR_FIELDS = Object.freeze({
  full: Object.freeze({
    period: true,
    environment: true,
    broadcast: true,
    program: true,
    campaign: true,
    gridClass: FULL_FILTER_GRID_CLASS,
  }),
  // DASH-EMAIL-P1-IMPL-006: Email Overview's own config -- consolidates
  // the real, governed EDT Broadcast/State filters (previously a separate
  // "Governed Delivered Filters" card below Overview, now retired) into
  // this single shared bar. `environment` here is the SAME field `full`
  // already used, now ALSO the authoritative EDT observed_ghl_environment
  // filter for Overview specifically (its value space -- all/
  // qualification/production -- already matched that real parameter
  // exactly; no rename, no new field needed, no risk to Performance's own
  // independent mock-adapter consumption of this same shared value).
  // `broadcast: false` -- Overview's primary-row Broadcast slot renders
  // the REAL governed selector (`governedBroadcastId`, real BRC#### ids
  // from `broadcastOptions`) instead of the mock Program->Campaign->
  // Broadcast-cascaded one `full` renders; the two are mutually exclusive
  // per view by construction (only one of `fields.broadcast`/
  // `fields.governedBroadcastId` is ever true for a given `fields`
  // config), so there is never a duplicate visible Broadcast control.
  // `program`/`campaign` stay true and stay wired to the SAME mock
  // PROGRAM_HIERARCHY `full` already used -- explicitly non-authoritative
  // for Overview's real metrics (never forwarded to EDT or LPE); only
  // their OWN dropdown continues to exist, unchanged, in the Advanced
  // row. `governedState` is new -- the real State filter, moved into the
  // Advanced row per this checkpoint's own target architecture. Reuses
  // FULL_FILTER_GRID_CLASS verbatim: the primary row's column template is
  // unchanged (still Email/Period/Environment/[Broadcast-slot]/Actions,
  // now with the governed selector occupying that slot); the State cell
  // lands on its own implicit Advanced-row line (CSS Grid auto-creates
  // it) rather than requiring a new static column literal.
  // DASH-EMAIL-P1-IMPL-032: `program`/`campaign` flip to false and
  // `governedAudienceId`/`governedProgramId`/`governedCampaignId` join
  // `governedBroadcastId`/`governedState` -- Overview's Advanced row now
  // shows the REAL, governed Program/Campaign/Audience selectors instead
  // of the mock PROGRAM_HIERARCHY-cascaded ones (mutually exclusive by
  // construction, same pattern `broadcast`/`governedBroadcastId` already
  // established: showing both a real and a non-authoritative "Program"
  // control side by side would be actively misleading, not merely
  // redundant). The mock `program`/`campaign` fields/state/PROGRAM_HIERARCHY
  // plumbing are NOT deleted -- `creativeAnalytics` below still uses them
  // (with its own, unrelated real hierarchy) -- only this config's own
  // flags change.
  overview: Object.freeze({
    period: true,
    environment: true,
    broadcast: false,
    governedBroadcastId: true,
    governedState: true,
    governedAudienceId: true,
    governedProgramId: true,
    governedCampaignId: true,
    program: false,
    campaign: false,
    gridClass: FULL_FILTER_GRID_CLASS,
  }),
  environmentOnly: Object.freeze({
    period: false,
    environment: true,
    broadcast: false,
    program: false,
    campaign: false,
    gridClass: ENVIRONMENT_ONLY_FILTER_GRID_CLASS,
  }),
  // DASH-EMAIL-P1-IMPL-008: Performance's original config -- Period was
  // the ONLY real, operational filter while EDT `/summary` and LPE
  // `/lpe-funnel` could not be scoped consistently (DASH-EMAIL-P1-
  // CHAR-007's "one filtered cohort" illusion). Retained, still defined,
  // for any future narrower surface that needs it -- Performance itself
  // now uses the `performance` config below (DASH-EMAIL-P1-IMPL-032:
  // #350/#351 removed that asymmetry, so both real contracts can now be
  // scoped consistently by the same seven filters).
  periodOnly: Object.freeze({
    period: true,
    environment: false,
    broadcast: false,
    program: false,
    campaign: false,
    gridClass: ENVIRONMENT_ONLY_FILTER_GRID_CLASS,
  }),
  // DASH-EMAIL-P1-IMPL-032: Performance's own config, now structurally
  // identical to `overview` above -- the same seven governed filters
  // (Period/Environment/Broadcast/Audience/Program/Campaign/State),
  // resolved through the SAME EDT `/summary` and LPE `/lpe-funnel`
  // contracts, mapped identically. A separate, independent literal (not
  // a reference to `overview`) -- this codebase's own established
  // convention (see this file's own header comment: "adding a
  // configuration means adding a complete literal, not parameterizing
  // this one") -- even though the two are currently identical in shape.
  performance: Object.freeze({
    period: true,
    environment: true,
    broadcast: false,
    governedBroadcastId: true,
    governedState: true,
    governedAudienceId: true,
    governedProgramId: true,
    governedCampaignId: true,
    program: false,
    campaign: false,
    gridClass: FULL_FILTER_GRID_CLASS,
  }),
  // P1 SLICE 3: the "Actions" view's own config -- structurally identical
  // to `overview`/`performance` above (the same seven governed filters,
  // resolved through the SAME LPE-side origin-attribution contract the
  // new /behavioral-actions endpoint shares with /lpe-funnel). A separate,
  // independent literal, per this file's own established convention (see
  // `performance`'s own comment above), even though all three are
  // currently identical in shape.
  behavioral: Object.freeze({
    period: true,
    environment: true,
    broadcast: false,
    governedBroadcastId: true,
    governedState: true,
    governedAudienceId: true,
    governedProgramId: true,
    governedCampaignId: true,
    program: false,
    campaign: false,
    gridClass: FULL_FILTER_GRID_CLASS,
  }),
  // DASH-EMAIL-004: Audience & Acquisition. Originally Period + Environment
  // only — Broadcast/Program/Campaign never had a defined meaning for
  // audience segmentation data (see emailAudienceMockAdapter.js).
  //
  // DASH-EMAIL-P1-IMPL-008: leadSource/acquisitionChannel/geography/
  // lifecycleState flipped to false -- DASH-EMAIL-P1-CHAR-007 found this
  // entire "audience segmentation" concept has no authoritative backend
  // source at all (a hardcoded fixture, not a shell-phase-for-a-real-
  // endpoint), so Audience's four contextual dimensions were retired
  // (EmailAudienceView.jsx now renders an honest unavailable state; their
  // own popover controls no longer render at all — see
  // emailViewConfig.js's `audience.contextual: []`).
  //
  // DASH-EMAIL-P1-IMPL-008A: Period/Environment ALSO flipped to false, and
  // this config renamed from `periodEnvironment` to `unavailable` -- the
  // prior name became actively misleading once neither was true. Audience
  // has no authoritative operational data of ANY kind (no KPI, no funnel,
  // no segmentation, no real fetch at all), so Period/Environment
  // remaining visible-but-inert was itself the exact "false operational
  // affordance" this correction removes -- a filter control implies
  // something filterable exists behind it, which is no longer true here.
  // With every dimension false, `hasAnyOperativeField` below also hides
  // Apply (Reset/Advanced already hide themselves via their own existing
  // predicates once every field is false) -- Audience renders ONLY the
  // Email: view selector, nothing else, via NO_OPERATIONAL_FILTERS_GRID_CLASS.
  unavailable: Object.freeze({
    period: false,
    environment: false,
    broadcast: false,
    program: false,
    campaign: false,
    leadSource: false,
    acquisitionChannel: false,
    geography: false,
    lifecycleState: false,
    gridClass: NO_OPERATIONAL_FILTERS_GRID_CLASS,
  }),
  // DASH-EMAIL-011a: Creative Analytics. Period/Program/Campaign (no
  // Broadcast — Creative Analytics' governed grains are Angle/Creative/
  // Asset, not individual Broadcasts) plus Angle, Creative Analytics' one
  // contextual dimension, committed by the SAME Apply button. Deliberately
  // a NEW config rather than reusing `full`: a dirty, un-applied
  // Creative-Analytics-only Angle draft must never leak into
  // Overview/Performance's isDirtyFor(full) gating (the exact isolation
  // hazard the field-scoped API exists to prevent).
  //
  // DASH-EMAIL-012A: `environment: false` — the real Broadcast catalog
  // (EMAIL-CATALOG-007A) has no `environment` field at all, so this
  // control is removed for Creative Analytics specifically rather than
  // kept as a visually-functional-looking no-op (DASH-EMAIL-012A Frozen
  // Decision 8). This is the field-scoped config architecture's exact
  // purpose — Overview/Performance's own `environment: true` is
  // completely untouched.
  //
  // Reuses PERIOD_ENVIRONMENT_FILTER_GRID_CLASS verbatim (Email/Period/
  // Environment/Actions — 4 columns) even though `environment` no longer
  // renders for this config: with BOTH `broadcast: false` and
  // `environment: false`, the Environment cell simply doesn't render
  // either, and the advanced row's Program/Campaign cells are still
  // hardcoded to `lg:col-start-3`/`lg:col-start-4` — those columns exist
  // in this literal regardless of which primary-row cells occupy them, so
  // Program/Campaign still land correctly without any JSX or literal
  // change. Angle never renders in the primary grid at all (contextual/
  // popover-only), so no new Tailwind literal is needed here either.
  creativeAnalytics: Object.freeze({
    period: true,
    environment: false,
    broadcast: false,
    program: true,
    campaign: true,
    angle: true,
    gridClass: PERIOD_ENVIRONMENT_FILTER_GRID_CLASS,
  }),
});

function FilterCell({ label, testId, className = "", children }) {
  return (
    <div className={`flex min-w-0 items-center gap-1.5 ${className}`.trim()}>
      <span className="shrink-0 text-sm text-slate-300">{label}</span>
      <div className="min-w-0 flex-1" data-testid={testId}>
        {children}
      </div>
    </div>
  );
}

/**
 * EmailFilterBar — Checkpoint 3.4E.
 *
 * The single, unified filter bar for both Email Overview and Email
 * Performance. Replaces the inline control-bar JSX that used to live
 * directly in EmailDashboardPanel.jsx plus the now-retired
 * EmailPerformanceFilters.jsx (Performance-only, three-in-a-row).
 *
 * Collapsed/default state — one primary grid row: Email, Period,
 * Environment, Broadcast, and an Actions cell (Apply + expand/collapse
 * toggle). Reset is NOT part of the Actions cell by default — it only
 * mounts once the draft differs from EMAIL_FILTER_DEFAULTS (see showReset
 * below), so it never occupies permanent horizontal space. Email remains
 * immediate view navigation (not Apply-controlled — `view`/`onViewChange`
 * bypass `filterState` entirely). Period, Environment, Broadcast, Program,
 * and Campaign are query filters routed through `filterState`
 * (useEmailFilterState) and only take effect on Apply.
 *
 * Expanded state reveals Program/Campaign as explicitly-placed cells of
 * the SAME grid (see FULL_FILTER_GRID_CLASS/EMAIL_FILTER_BAR_FIELDS) —
 * Program at column 3 (Environment's column), Campaign at column 4
 * (Broadcast's column) — see
 * that constant's comment for why one shared grid, rather than two
 * independent rows, is what actually guarantees this alignment. Nothing
 * can land under Email or Period: those columns are simply never
 * targeted by any advanced-row cell. Collapsing does not clear their
 * draft selections; a restrained dot indicator on the expand toggle shows
 * when either carries a non-default value while collapsed.
 *
 * Selecting the Period "Custom" option reveals a compact Start/End date
 * row (native date inputs) directly beneath the primary row, independent
 * of the advanced-filter expand/collapse — choosing Custom is itself an
 * explicit request to see the range inputs, not something that should
 * also require a second, unrelated expand click.
 */
export default function EmailFilterBar({
  view,
  onViewChange,
  programs,
  angleOptions,
  // DASH-EMAIL-P1-IMPL-006: the real, governed Broadcast catalog
  // (`{broadcast_id, name}[]`, from `getBroadcasts()`) -- structurally
  // distinct from `programs` above (that stays the mock Program ->
  // Campaign -> Broadcast hierarchy `full`/Performance uses). Only
  // consulted when `fields.governedBroadcastId` is true.
  broadcastOptions,
  // DASH-EMAIL-P1-IMPL-032: the real, governed Audience-Definition catalog
  // (`{audience_id, name, status}[]`, from `getEmailMarketingAudienceDefinitions()`)
  // -- only consulted when `fields.governedAudienceId` is true. Same
  // one-time-reference-data-fetch pattern as `broadcastOptions`.
  audienceOptions,
  filterState,
  fields = EMAIL_FILTER_BAR_FIELDS.full,
}) {
  const [expanded, setExpanded] = useState(false);
  const {
    draft,
    isValid,
    hasHiddenActiveFilters,
    setPeriod,
    setCustomRange,
    setEnvironment,
    setProgram,
    setCampaign,
    setBroadcast,
    setGovernedBroadcastId,
    setGovernedState,
    setGovernedAudienceId,
    setGovernedProgramId,
    setGovernedCampaignId,
    isDirtyFor,
    isDraftAtDefaultsFor,
    isValidForFields,
    applyFields,
    resetFields,
  } = filterState;

  // Checkpoint 3.4F: Reset's visibility is intentionally keyed off
  // draft-vs-GLOBAL-DEFAULTS, not draft-vs-applied. Draft-vs-applied would
  // go false the instant Apply is clicked — even when the just-applied
  // filters are non-default — which would make Reset disappear right when
  // it's still the only way to get back to defaults. See
  // useEmailFilterState.js for the full scenario walkthrough this
  // predicate is required to satisfy.
  //
  // Checkpoint 3.5A remediation: uses the field-scoped isDraftAtDefaultsFor
  // uniformly (for every `fields` config, including "full") rather than
  // branching between the whole-draft isDraftAtDefaults and a locally
  // reimplemented environment-only check. isDraftAtDefaultsFor(fields) is
  // mathematically identical to the old whole-draft comparison when
  // `fields` is EMAIL_FILTER_BAR_FIELDS.full (every field true) — same
  // per-field comparisons, same customRange gate — so Overview/Performance
  // get byte-for-byte the same answer as 3.4F. For environmentOnly, this
  // correctly ignores whatever Program/Campaign/Broadcast/Period a
  // DIFFERENT view left non-default in the shared draft.
  const showReset = !isDraftAtDefaultsFor(fields);
  const applyDisabled = !isValidForFields(fields) || !isDirtyFor(fields);

  // DASH-EMAIL-P1-IMPL-008A: Apply was previously unconditional -- correct
  // for every existing config (each has always had at least one operative
  // dimension), but wrong for a view with NONE (Audience's new
  // `unavailable` config): a permanently-disabled-but-visible Apply button
  // still implies something is filterable here, which this correction
  // requires removing entirely, not merely disabling. `hasAnyOperativeField`
  // is derived generically from `fields` itself (any boolean flag other
  // than the non-boolean `gridClass`), so a future all-false config gets
  // this behavior automatically, with no per-config special-casing.
  const hasAnyOperativeField = Object.entries(fields).some(([key, value]) => key !== "gridClass" && value === true);

  // Advanced only exists to reveal Program/Campaign/(DASH-EMAIL-P1-IMPL-006)
  // governedState — a config that hides all three (e.g. Deliverability)
  // must not render a disclosure control with nothing behind it to
  // disclose.
  const showAdvanced =
    fields.program ||
    fields.campaign ||
    fields.governedState ||
    fields.governedAudienceId ||
    fields.governedProgramId ||
    fields.governedCampaignId;

  const options = programs || [];
  const selectedProgram = options.find((p) => p.id === draft.program);
  const campaignOptions = selectedProgram?.campaigns || [];
  const selectedCampaign = campaignOptions.find((c) => c.id === draft.campaign);

  // Broadcast lives in the always-visible primary row, while Program/
  // Campaign live in the collapsed-by-default advanced row — so unlike
  // Performance's pre-3.4E behavior (Broadcast disabled until a Campaign
  // is chosen), Broadcast must stay directly usable without requiring the
  // user to expand Advanced first. Its OPTION LIST still narrows exactly
  // as before when Program/Campaign are set (dependent-filter semantics
  // preserved); the only change is that "no Campaign chosen yet" now
  // means "show every broadcast" (flattened) instead of "show none,
  // disabled."
  // DASH-EMAIL-P1-IMPL-006: renamed from `broadcastOptions` to
  // `mockBroadcastOptions` to free that name for the new real,
  // governed-catalog PROP above -- this local variable is still the
  // exact same mock Program/Campaign-cascaded derivation, unchanged.
  const mockBroadcastOptions = selectedCampaign
    ? selectedCampaign.broadcasts
    : selectedProgram
      ? selectedProgram.campaigns.flatMap((c) => c.broadcasts)
      : options.flatMap((p) => p.campaigns.flatMap((c) => c.broadcasts));

  // DASH-EMAIL-P1-IMPL-032: the REAL, governed Program/Campaign filters --
  // occupy the SAME advanced-row slots `fields.program`/`fields.campaign`
  // above do; mutually exclusive by construction (a `fields` config never
  // sets both a mock and its governed counterpart true), so these are
  // never a second, visible Program/Campaign control alongside the mock
  // ones. Reuses the SAME `programs` prop shape (`{id, label,
  // campaigns:[{id, label, broadcasts:[]}]}`) `options` above already
  // derives from -- when `fields.governedProgramId`/`governedCampaignId`
  // are true, the caller passes a REAL hierarchy (real PRG###/CMP####
  // ids) through this same prop, never the mock PROGRAM_HIERARCHY.
  // Deliberately FLAT, independent lists (every Campaign across every
  // Program, never narrowed by the selected governed Program) -- per this
  // checkpoint's own frozen decision against client-side cascading:
  // selecting a governed Program must not narrow the governed Campaign
  // option list (that would be exactly the "unsupported client-side
  // cascade" this checkpoint forbids), even though the mock Program/
  // Campaign cascade above still does, for the configs that still use it.
  const governedProgramOptions = options;
  const governedCampaignOptions = options.flatMap((p) => p.campaigns);

  // A Broadcast chosen directly from the primary row (Program/Campaign
  // still "all") reverse-populates Program/Campaign so the advanced row,
  // if the user later expands it, shows a consistent, non-misleading
  // selection instead of "All Programs / All Campaigns" alongside a
  // specific Broadcast. setProgram/setCampaign each cascade-reset
  // downstream fields to "all" (existing, preserved semantics) — calling
  // them in Program -> Campaign -> Broadcast order lets each subsequent
  // call's explicit value survive the previous call's reset, composing
  // to the correct final { program, campaign, broadcast } in one batch.
  //
  // Checkpoint 3.4E remediation (hardening): resolution now happens via a
  // single upfront lookup (findBroadcastParent) instead of resolving and
  // acting inside the same nested loop, so there is one unambiguous
  // "exactly one parent, or none" decision point. Multiple matches cannot
  // happen — emailPerformanceMockAdapter.js asserts catalog-wide broadcast
  // id uniqueness at module load, so a duplicate id fails loudly at
  // import time, long before this code could ever run against it. If a
  // broadcast id genuinely resolves to NO parent (unreachable through the
  // UI today, since this select's own options are generated from the same
  // catalog — only reachable via a non-UI/programmatic call), Program and
  // Campaign are explicitly reset to "all" rather than left stale, so the
  // resulting draft is always a coherent triple (never a mismatched
  // leftover Program/Campaign paired with an unrelated Broadcast).
  const findBroadcastParent = (broadcastId) => {
    for (const p of options) {
      for (const c of p.campaigns) {
        if (c.broadcasts.some((b) => b.id === broadcastId)) {
          return { programId: p.id, campaignId: c.id };
        }
      }
    }
    return null;
  };

  const handleBroadcastChange = (broadcastId) => {
    if (broadcastId === "all") {
      setBroadcast("all");
      return;
    }
    const parent = findBroadcastParent(broadcastId);
    if (parent) {
      setProgram(parent.programId);
      setCampaign(parent.campaignId);
      setBroadcast(broadcastId);
      return;
    }
    // Not found in the catalog: reset Program/Campaign to "all" rather
    // than leaving a stale, now-inconsistent selection in place, and set
    // the Broadcast anyway rather than silently dropping the user's
    // input — the mock adapter's own filter simply won't match any row.
    setProgram("all");
    setCampaign("all");
    setBroadcast(broadcastId);
  };

  const isCustomPeriod = draft.period === "custom";
  const customRangeInvalid = isCustomPeriod && !isValid;

  return (
    <Card className="glass border-white/10" data-testid="email-contextual-header">
      <CardContent className="flex flex-col gap-3 p-4">
        {/* The single shared grid — see fields.gridClass (a complete
            static Tailwind literal, see EMAIL_FILTER_BAR_FIELDS above).
            Row 1 (Email/[Period]/[Environment]/[Broadcast]/Actions,
            bracketed cells conditional on `fields`) is auto-placed by DOM order into
            however many columns this variant renders; the advanced row's
            Program/Campaign cells are explicit children of this SAME grid,
            placed via lg:col-start-3 / lg:col-start-4 so they inherit row
            1's actual column boundaries rather than approximating them
            (see EMAIL_FILTER_BAR_FIELDS's comment for why this hardcoded
            3/4 stays correct even though the grid itself is now variable). */}
        <div className={fields.gridClass} data-testid="email-control-row">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="shrink-0 text-sm text-slate-300">Email:</span>
            <EmailViewSelector view={view} onChange={onViewChange} />
          </div>

          {fields.period && (
            <FilterCell label="Period:" testId="email-filter-period-cell">
              <Select value={draft.period} onValueChange={setPeriod}>
                <SelectTrigger
                  className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                  data-testid="email-filter-period-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* EMAIL-DASHBOARD-PERIOD-002: expanded named-period
                      preset list, in the exact authorized display order.
                      Values are the SAME canonical period keys
                      resolve_dashboard_reporting_period (backend) already
                      resolves -- "mtd"/"ytd" are deliberately UNCHANGED
                      keys (only their display labels became "This Month"/
                      "This Year") to avoid unnecessary API-contract churn;
                      "7d"/"30d" (Last 7/30 Days) are the pre-existing
                      rolling windows, unchanged. "Month to Date"/"Year to
                      Date" are retired display labels -- never shown
                      again, though their underlying keys persist. */}
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="last_week">Last Week</SelectItem>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="mtd">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="this_quarter">This Quarter</SelectItem>
                  <SelectItem value="last_quarter">Last Quarter</SelectItem>
                  <SelectItem value="ytd">This Year</SelectItem>
                  <SelectItem value="last_year">Last Year</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </FilterCell>
          )}

          {fields.environment && (
            <FilterCell label="Environment:" testId="email-filter-environment-cell">
              <Select value={draft.environment} onValueChange={setEnvironment}>
                <SelectTrigger
                  className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                  data-testid="email-filter-environment-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="qualification">Qualification</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </FilterCell>
          )}

          {fields.broadcast && (
            <FilterCell label="Broadcast:" testId="email-filter-broadcast-cell">
              <Select value={draft.broadcast} onValueChange={handleBroadcastChange}>
                <SelectTrigger
                  className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                  data-testid="email-filter-broadcast-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Broadcasts</SelectItem>
                  {mockBroadcastOptions.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterCell>
          )}

          {/* DASH-EMAIL-P1-IMPL-006: the REAL, governed Broadcast filter --
              occupies the exact same primary-row slot `fields.broadcast`
              above does; mutually exclusive by construction (a `fields`
              config never sets both true), so this is never a second
              visible Broadcast control alongside the mock one. Options
              come from `broadcastOptions` (real `{broadcast_id, name}`
              catalog), never the mock Program/Campaign hierarchy -- no
              cascading, no Program/Campaign reverse-population (those stay
              explicitly non-authoritative for Overview's real metrics). */}
          {fields.governedBroadcastId && (
            <FilterCell label="Broadcast:" testId="email-filter-governed-broadcast-cell">
              <Select value={draft.governedBroadcastId} onValueChange={setGovernedBroadcastId}>
                <SelectTrigger
                  className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                  data-testid="email-filter-governed-broadcast-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Broadcasts</SelectItem>
                  {(broadcastOptions || []).map((b) => (
                    <SelectItem key={b.broadcast_id} value={b.broadcast_id}>
                      {b.name} ({b.broadcast_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterCell>
          )}

          {/* Actions — one grid cell (last column), its own internal flex
              row. Reset mounting/unmounting only rearranges content
              INSIDE this cell; it cannot change which grid column Program/
              Campaign resolve to, since column identity comes from
              lg:col-start-3/4 on those cells, not from anything here. */}
          <div className="flex items-center justify-end gap-3" data-testid="email-filter-actions">
            {showReset && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 text-slate-300 hover:text-slate-100"
                onClick={() => resetFields(fields)}
                data-testid="email-filter-reset"
              >
                Reset
              </Button>
            )}

            {hasAnyOperativeField && (
              <Button
                type="button"
                variant="default"
                size="sm"
                className="shrink-0"
                onClick={() => applyFields(fields)}
                disabled={applyDisabled}
                data-testid="email-filter-apply"
              >
                Apply
              </Button>
            )}

            {showAdvanced && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="relative shrink-0 gap-1.5 border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-slate-100"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-controls="email-advanced-row"
                aria-label={expanded ? "Hide advanced filters" : "Show advanced filters"}
                data-testid="email-filter-expand-toggle"
              >
                Advanced
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                  data-testid="email-filter-expand-chevron"
                />
                {hasHiddenActiveFilters && !expanded && (
                  <span
                    className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-indigo-400 ring-2 ring-slate-950"
                    data-testid="email-filter-hidden-indicator"
                    aria-hidden="true"
                  />
                )}
              </Button>
            )}

            <EmailFiltersControl view={view} filterState={filterState} angleOptions={angleOptions} />
          </div>

          {/* Advanced cells — explicit children of the SAME grid as row 1.
              className="contents" keeps this wrapper addressable (id/
              data-testid, for aria-controls and tests) without it becoming
              a box of its own — its children are promoted to be direct
              grid items, so lg:col-start-3/4 place them in Environment's
              and Broadcast's actual columns, not merely a visually-similar
              approximation. */}
          {showAdvanced && expanded && (
            <div className="contents" id="email-advanced-row" data-testid="email-advanced-row">
              {/* Mock, PROGRAM_HIERARCHY-cascaded Program/Campaign -- still
                  used by creativeAnalytics (its own real, unrelated
                  hierarchy passed through the same `programs` prop).
                  Individually gated (previously unconditional) now that
                  `overview`/`performance` render the governed pair below
                  in these exact same grid slots instead -- mutually
                  exclusive by construction, same reasoning as
                  `broadcast`/`governedBroadcastId`. */}
              {fields.program && (
                <FilterCell
                  label="Program:"
                  testId="email-filter-program-cell"
                  className="lg:col-start-3 lg:row-start-2"
                >
                  <Select value={draft.program} onValueChange={setProgram}>
                    <SelectTrigger
                      className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                      data-testid="email-filter-program-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Programs</SelectItem>
                      {options.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterCell>
              )}

              {fields.campaign && (
                <FilterCell
                  label="Campaign:"
                  testId="email-filter-campaign-cell"
                  className="lg:col-start-4 lg:row-start-2"
                >
                  <Select value={draft.campaign} onValueChange={setCampaign} disabled={draft.program === "all"}>
                    <SelectTrigger
                      className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 disabled:opacity-50 ${SELECT_FOCUS_RING_FIX}`}
                      data-testid="email-filter-campaign-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Campaigns</SelectItem>
                      {campaignOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterCell>
              )}

              {/* DASH-EMAIL-P1-IMPL-032: the REAL, governed Program filter --
                  occupies the exact same slot `fields.program` above does.
                  Options come from the SAME `programs` prop, but the
                  caller passes a real hierarchy (real PRG### ids) for a
                  config with this flag true -- never the mock
                  PROGRAM_HIERARCHY. */}
              {fields.governedProgramId && (
                <FilterCell
                  label="Program:"
                  testId="email-filter-governed-program-cell"
                  className="lg:col-start-3 lg:row-start-2"
                >
                  <Select value={draft.governedProgramId} onValueChange={setGovernedProgramId}>
                    <SelectTrigger
                      className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                      data-testid="email-filter-governed-program-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Programs</SelectItem>
                      {/* p.id (the real, stable PRG### operational ID) is
                          the SelectItem's value -- the only thing ever
                          sent as a filter. p.label (the mutable display
                          name) is the label only -- names are never sent
                          as a filter value. */}
                      {governedProgramOptions.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterCell>
              )}

              {/* DASH-EMAIL-P1-IMPL-032: the REAL, governed Campaign filter
                  -- occupies the exact same slot `fields.campaign` above
                  does. Deliberately NEVER disabled by/narrowed on the
                  governed Program selection -- independent selectors, no
                  client-side cascade (this checkpoint's own frozen
                  decision); the backend AND-composes both when supplied. */}
              {fields.governedCampaignId && (
                <FilterCell
                  label="Campaign:"
                  testId="email-filter-governed-campaign-cell"
                  className="lg:col-start-4 lg:row-start-2"
                >
                  <Select value={draft.governedCampaignId} onValueChange={setGovernedCampaignId}>
                    <SelectTrigger
                      className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                      data-testid="email-filter-governed-campaign-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Campaigns</SelectItem>
                      {governedCampaignOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterCell>
              )}

              {/* DASH-EMAIL-P1-IMPL-006: the real, governed State filter --
                  moved into the Advanced row per this checkpoint's target
                  architecture. Individually gated (unlike Program/Campaign
                  above, which every showAdvanced-triggering config today
                  happens to pair together) so a config like
                  creativeAnalytics -- which shows Program/Campaign but has
                  no governedState concept at all -- never renders this
                  cell. Lands on its own implicit third grid row, under
                  Environment's column. */}
              {fields.governedState && (
                <FilterCell
                  label="State:"
                  testId="email-filter-governed-state-cell"
                  className="lg:col-start-3 lg:row-start-3"
                >
                  <Select value={draft.governedState} onValueChange={setGovernedState}>
                    <SelectTrigger
                      className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                      data-testid="email-filter-governed-state-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All States</SelectItem>
                      {US_STATE_OPTIONS.map(([code, name]) => (
                        <SelectItem key={code} value={code}>
                          {name} ({code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterCell>
              )}

              {/* DASH-EMAIL-P1-IMPL-032: the real, governed Audience filter
                  -- lands on its own cell under Broadcast's column,
                  alongside governedState under Environment's column, on
                  this same implicit third grid row. Independent of every
                  other selector -- no cascade into or out of it. */}
              {fields.governedAudienceId && (
                <FilterCell
                  label="Audience:"
                  testId="email-filter-governed-audience-cell"
                  className="lg:col-start-4 lg:row-start-3"
                >
                  <Select value={draft.governedAudienceId} onValueChange={setGovernedAudienceId}>
                    <SelectTrigger
                      className={`w-full min-w-0 flex-1 bg-white/5 text-slate-100 border-white/10 ${SELECT_FOCUS_RING_FIX}`}
                      data-testid="email-filter-governed-audience-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Audiences</SelectItem>
                      {(audienceOptions || []).map((a) => (
                        <SelectItem key={a.audience_id} value={a.audience_id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FilterCell>
              )}
            </div>
          )}
        </div>

        {/* Custom Period range — appears whenever Custom is selected,
            independent of the advanced-filter expand/collapse toggle. Now
            a sibling rendered after the whole grid (rather than sandwiched
            between two independent rows, which no longer exist as
            separate containers) — still visually beneath the primary
            controls; a trivial, non-semantic container adjustment.
            Checkpoint 3.5A: gated on fields.period too — filterState is
            shared across views, so draft.period could already be "custom"
            from an earlier Overview/Performance visit even on a view that
            doesn't render a Period control at all; without this guard the
            date-range row could incorrectly appear there. */}
        {fields.period && isCustomPeriod && (
          <div className="flex flex-wrap items-center gap-3" data-testid="email-filter-custom-range-row">
            <div className="flex items-center gap-1.5">
              {/* Checkpoint 3.4E remediation: real <label htmlFor> instead
                  of a plain visual span — native <input type="date"> gets
                  no accessible name "for free" from its value the way a
                  Select trigger does from its visible option text, so an
                  unassociated label left these fields unnamed to a screen
                  reader. */}
              <label htmlFor="email-filter-custom-start" className="shrink-0 text-sm text-slate-300">
                Start:
              </label>
              <Input
                id="email-filter-custom-start"
                type="date"
                className="w-[160px]"
                value={draft.customRange?.startDate || ""}
                onChange={(e) =>
                  setCustomRange({ ...(draft.customRange || {}), startDate: e.target.value || undefined })
                }
                data-testid="email-filter-custom-start"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label htmlFor="email-filter-custom-end" className="shrink-0 text-sm text-slate-300">
                End:
              </label>
              <Input
                id="email-filter-custom-end"
                type="date"
                className="w-[160px]"
                value={draft.customRange?.endDate || ""}
                onChange={(e) =>
                  setCustomRange({ ...(draft.customRange || {}), endDate: e.target.value || undefined })
                }
                data-testid="email-filter-custom-end"
              />
            </div>
            {customRangeInvalid && (
              <span className="text-xs text-red-300" data-testid="email-filter-custom-range-error">
                Select a start and end date, with start on or before end.
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
